import { useResizeObserver } from '@op/react-web'
import { useEffect } from 'react'

type LineClampProps = {
	readonly className?: string
	readonly ellipsis?: string
	readonly maxLines?: number
	readonly text: string
}

export const LineClamp = ({ className, ellipsis = '…', maxLines = 3, text }: LineClampProps) => {
	const [$paragraphElCbRef, $paragraphElRef] = useResizeObserver<HTMLParagraphElement>(() => {
		clampLines($paragraphElRef.current, maxLines, ellipsis)
	})

	// Recheck if line clamp is required if text, ellipsis, or max lines change
	useEffect(() => {
		clampLines($paragraphElRef.current, maxLines, ellipsis)
	}, [className, ellipsis, maxLines, text])

	return (
		<p className={className} ref={$paragraphElCbRef} title={text}>
			{text}
		</p>
	)
}

const getLineHeight = ($el: HTMLParagraphElement) => {
	const lh = Number.parseFloat(window.getComputedStyle($el).lineHeight)
	if (!Number.isNaN(lh)) return lh

	// Fallback for line-height: normal
	const probe = document.createElement('span')
	probe.textContent = 'M'
	$el.appendChild(probe)
	const h = probe.getBoundingClientRect().height
	probe.remove()
	return h
}

const clampLines = ($el: HTMLParagraphElement | null, lines: number, ellipsis = '…') => {
	if (!$el) return

	const fullText = $el.textContent || ''
	const lh = getLineHeight($el)
	const maxHeight = Math.round(lh * lines)

	if ($el.scrollHeight <= maxHeight) return

	let low = 0
	let high = fullText.length

	while (low < high) {
		const mid = (low + high + 1) >> 1
		$el.textContent = fullText.slice(0, mid) + ellipsis
		if ($el.scrollHeight <= maxHeight) low = mid
		else high = mid - 1
	}

	$el.textContent = fullText.slice(0, low) + ellipsis
}
